import { useEffect, useState } from 'react';
import { Box, Typography, Avatar, CircularProgress, Chip, Stack } from '@mui/material';
import { Title, useDataProvider } from 'react-admin';
import { Person as PersonIcon, Email as EmailIcon, Public as PublicIcon } from '@mui/icons-material';
import { InfoCard, InfoField, resources, useUserRoles, useUserTerritoryChoices, useUserCompanyChoices, useUserLobChoices } from '@repo/c360-ra-components';

export const Profile = () => {
  const { roles } = useUserRoles();
  const { choices: territoryChoices } = useUserTerritoryChoices();
  const { choices: companyChoices } = useUserCompanyChoices();
  const { choices: lobChoices } = useUserLobChoices();
  const dataProvider = useDataProvider();
  const userId = localStorage.getItem('userId');

  const [adminUser, setAdminUser] = useState<Record<string, unknown> | null>(null);
  const [isAdminUserLoading, setIsAdminUserLoading] = useState(false);

  useEffect(() => {
    if (!userId) {
      setAdminUser(null);
      return;
    }
    const loadAdminUser = async () => {
      setIsAdminUserLoading(true);
      try {
        const result = await dataProvider.getOne(resources.adminUsers, { id: userId });
        setAdminUser(result?.data ?? null);
      } catch (error) {
        console.error('Failed to load admin user:', error);
        setAdminUser(null);
      } finally {
        setIsAdminUserLoading(false);
      }
    };
    loadAdminUser();
  }, [dataProvider, userId]);

  const user = adminUser;

  if (isAdminUserLoading || !adminUser) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 'calc(100vh - 64px)',
          width: '100%',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const firstName = user?.['firstName'] as string | undefined;
  const lastName = user?.['lastName'] as string | undefined;
  const email = user?.['email'] as string | undefined;
  const avatar = user?.['avatar'] as string | undefined;
  const id = (user?.['id'] ?? user?.['_id']) as string | undefined;

  const fullName = [firstName, lastName].filter(Boolean).join(' ') || 'Unknown';
  const initials = [firstName?.[0], lastName?.[0]].filter(Boolean).join('').toUpperCase() || '?';

  return (
    <>
      <Title title="My Profile" />
      <Box
        sx={{
          width: '100%',
          minHeight: 'calc(100vh - 64px)',
          py: 4,
          px: 3,
          boxSizing: 'border-box',
        }}
      >
        <Typography
          variant="h4"
          component="h2"
          sx={{
            mb: 4,
            fontWeight: 600,
            color: 'text.primary',
            letterSpacing: '-0.01em',
            fontSize: '1.5rem',
          }}
        >
          My Profile
        </Typography>

        {isAdminUserLoading && !adminUser ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                alignItems: { xs: 'flex-start', md: 'center' },
                gap: 3,
                mb: 4,
                pb: 3,
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                  {fullName}
                </Typography>
                {/* <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                  {id}
                </Typography> */}
              </Box>
            </Box>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
                gap: 3,
              }}
            >
              <InfoCard title="Account Information" icon={<PersonIcon sx={{ color: 'text.secondary' }} />}>
                <InfoField label="FIRST NAME" value={firstName || 'N/A'} />
                <InfoField label="LAST NAME" value={lastName || 'N/A'} />
                <InfoField label="USER ID" value={String(id || 'N/A')} />
                <Box sx={{ mb: 1.75 }}>
                  <Typography
                    variant="overline"
                    sx={{
                      color: 'text.secondary',
                      fontWeight: 600,
                      letterSpacing: '0.08em',
                      lineHeight: 1.6,
                    }}
                  >
                    ROLES
                  </Typography>
                  <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mt: 0.75 }}>
                    {Array.isArray(roles) && roles.length > 0 ? (
                      roles
                        .map((r) => ({
                          key: typeof r === 'object' && r?._id ? r._id : String(r),
                          name: typeof r === 'object' && r?.name ? r.name : String(r),
                        }))
                        .filter((r) => r.name)
                        .map(({ key, name }) => (
                          <Chip
                            key={key}
                            label={name}
                            size="small"
                            variant="outlined"
                            sx={{
                              fontWeight: 500,
                              borderColor: 'primary.main',
                              color: 'primary.main',
                              '& .MuiChip-label': { px: 1.25 },
                            }}
                          />
                        ))
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                        No roles assigned
                      </Typography>
                    )}
                  </Stack>
                </Box>
              </InfoCard>

              <InfoCard title="Contact" icon={<EmailIcon sx={{ color: 'text.secondary' }} />}>
                <InfoField label="EMAIL" value={email || 'N/A'} />
              </InfoCard>

              <InfoCard title="Access Levels" icon={<PublicIcon sx={{ color: 'text.secondary' }} />} sx={{ gridColumn: { xs: '1', md: '1 / -1' } }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                  <Box>
                    <Typography
                      variant="overline"
                      sx={{
                        color: 'text.secondary',
                        fontWeight: 600,
                        letterSpacing: '0.08em',
                        lineHeight: 1.6,
                      }}
                    >
                      TERRITORIES
                    </Typography>
                    <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mt: 0.75 }}>
                      {territoryChoices.length > 0 ? (
                        territoryChoices.map(({ id, name }) => (
                          <Chip
                            key={id}
                            label={name}
                            size="small"
                            variant="outlined"
                            sx={{
                              fontWeight: 500,
                              borderColor: 'primary.main',
                              color: 'primary.main',
                              '& .MuiChip-label': { px: 1.25 },
                            }}
                          />
                        ))
                      ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                          No territories assigned
                        </Typography>
                      )}
                    </Stack>
                  </Box>
                  <Box>
                    <Typography
                      variant="overline"
                      sx={{
                        color: 'text.secondary',
                        fontWeight: 600,
                        letterSpacing: '0.08em',
                        lineHeight: 1.6,
                      }}
                    >
                      COMPANIES
                    </Typography>
                    <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mt: 0.75 }}>
                      {companyChoices.length > 0 ? (
                        companyChoices.map(({ id, name }) => (
                          <Chip
                            key={id}
                            label={name}
                            size="small"
                            variant="outlined"
                            sx={{
                              fontWeight: 500,
                              borderColor: 'primary.main',
                              color: 'primary.main',
                              '& .MuiChip-label': { px: 1.25 },
                            }}
                          />
                        ))
                      ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                          No companies assigned
                        </Typography>
                      )}
                    </Stack>
                  </Box>
                  <Box>
                    <Typography
                      variant="overline"
                      sx={{
                        color: 'text.secondary',
                        fontWeight: 600,
                        letterSpacing: '0.08em',
                        lineHeight: 1.6,
                      }}
                    >
                      LINES OF BUSINESS
                    </Typography>
                    <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mt: 0.75 }}>
                      {lobChoices.length > 0 ? (
                        lobChoices.map(({ id, name }) => (
                          <Chip
                            key={String(id)}
                            label={String(name)}
                            size="small"
                            variant="outlined"
                            sx={{
                              fontWeight: 500,
                              borderColor: 'primary.main',
                              color: 'primary.main',
                              '& .MuiChip-label': { px: 1.25 },
                            }}
                          />
                        ))
                      ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                          No lines of business assigned
                        </Typography>
                      )}
                    </Stack>
                  </Box>
                </Box>
              </InfoCard>
            </Box>
          </>
        )}
      </Box>
    </>
  );
};
